import { useTranslation } from 'react-i18next';
import { AccordionDetails, Divider, Grid, Typography } from '@mui/material';
import React from 'react';
import { Accordion, AccordionPanelsNames, AccordionSummary } from '../Accordion';
import { CommunicationTrackingSummary } from './CommunicationTrackingSummary';
import { CommunicationTrackingRow } from './CommunicationTrackingRow';
import { useCommunicationTracking } from '../../../../../hooks/useCommunicationTracking';
import { CommunicationTrackingEventOut, CommunicationTrackingEventType } from '../../../../../api';

type CommunicationTrackingPanelProps = {
  expanded?: AccordionPanelsNames;
  handleChange: any;
  orderId?: string;
  voucherId?: string;
};

export const CommunicationTrackingPanel = ({
  expanded,
  handleChange,
  orderId,
  voucherId,
}: CommunicationTrackingPanelProps) => {
  const { t } = useTranslation();
  const { status, loading, error } = useCommunicationTracking(orderId, voucherId);

  const getCommunicationType = (eventType: CommunicationTrackingEventType): string => {
    if (eventType.startsWith('email_')) return 'email';
    if (eventType.startsWith('sms_')) return 'sms';
    if (eventType.startsWith('whatsapp_')) return 'whatsapp';
    return 'other';
  };

  const getTypeDisplayName = (type: string): string => {
    switch (type) {
      case 'email':
        return t('emailTrackingPanel.email', 'Email');
      case 'sms':
        return t('emailTrackingPanel.sms', 'SMS');
      case 'whatsapp':
        return t('emailTrackingPanel.whatsapp', 'WhatsApp');
      default:
        return t('emailTrackingPanel.other', 'Other');
    }
  };

  const groupEventsByType = (events: CommunicationTrackingEventOut[]) => {
    const grouped = events.reduce((acc, event) => {
      const type = getCommunicationType(event.event_type);
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(event);
      return acc;
    }, {} as Record<string, CommunicationTrackingEventOut[]>);

    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  };

  if (!orderId && !voucherId) {
    return null;
  }

  return (
    <Accordion
      onChange={handleChange}
      expandedPanelName={expanded}
      panelName={AccordionPanelsNames.CommunicationTrackingPanel}>
      <AccordionSummary
        title={t('emailTrackingPanel.title')}
        summaryComponent={<CommunicationTrackingSummary status={status} loading={loading} />}
      />
      <AccordionDetails>
        <Grid container flexDirection="column" gap={2}>
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}

          {!loading && !error && status.events.length === 0 && (
            <Typography color="text.secondary" variant="body2">
              {t('emailTrackingPanel.noEventsFound')}
            </Typography>
          )}

          {!loading && !error && status.events.length > 0 && (
            <>
              <Grid container gap={1} pb={1}>
                <Grid item xs={0.5}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" />
                </Grid>
                <Grid item xs={2.5}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('emailTrackingPanel.time')}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('emailTrackingPanel.emailType')}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('emailTrackingPanel.status')}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('emailTrackingPanel.details')}
                  </Typography>
                </Grid>
              </Grid>
              <Divider />

              {groupEventsByType(status.events).map(([type, events], groupIndex) => (
                <React.Fragment key={type}>
                  <Typography
                    variant="subtitle2"
                    color="primary"
                    fontWeight="bold"
                    sx={{ mt: groupIndex > 0 ? 2 : 1, mb: 1 }}>
                    {getTypeDisplayName(type)}
                  </Typography>
                  {events.map((event, index) => (
                    <CommunicationTrackingRow
                      key={`${event.sendgrid_email_data?.sg_message_id || event.twilio_sms_data?.message_sid}-${
                        event.timestamp
                      }-${index}`}
                      event={event}
                    />
                  ))}
                  {groupIndex < groupEventsByType(status.events).length - 1 && <Divider sx={{ mt: 1 }} />}
                </React.Fragment>
              ))}
            </>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
